import React, { useRef } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner, Text } from '../../components/ui'

type ContatosPortalTableSectionProps = {
  loading: boolean
  error?: string | null
  hasSearched: boolean
  isEmpty: boolean
  emptyBeforeSearchMessage: string
  emptyAfterSearchMessage?: string
  children: React.ReactNode
  currentPage: number
  totalPages: number
  total: number
  pageSize: number
  onPageChange: (page: number) => void
  /** Controles de rolagem horizontal (tabelas largas). Padrão: true. */
  showHorizontalScroll?: boolean
  /** Conteúdo à esquerda da barra superior (ex.: texto de ajuda + ordenação). */
  toolbar?: React.ReactNode
}

export function ContatosPortalTableSection({
  loading,
  error,
  hasSearched,
  isEmpty,
  emptyBeforeSearchMessage,
  emptyAfterSearchMessage = 'Nenhum registro encontrado.',
  children,
  currentPage,
  totalPages,
  total,
  pageSize,
  onPageChange,
  showHorizontalScroll = true,
  toolbar,
}: ContatosPortalTableSectionProps) {
  const listScrollRef = useRef<HTMLDivElement | null>(null)

  const scrollListByColumns = (direction: -1 | 1) => {
    const container = listScrollRef.current
    if (!container) return

    const headers = Array.from(
      container.querySelectorAll<HTMLElement>('thead th')
    )
    if (headers.length === 0) {
      container.scrollBy({ left: direction * 320, behavior: 'smooth' })
      return
    }

    const stickyWidth = headers
      .filter((th) => getComputedStyle(th).position === 'sticky')
      .reduce((sum, th) => sum + th.offsetWidth, 0)
    const columns = headers.filter((th) => getComputedStyle(th).position !== 'sticky')
    if (columns.length === 0) {
      container.scrollBy({ left: direction * 320, behavior: 'smooth' })
      return
    }

    // Alinha a próxima/anterior coluna logo após as colunas sticky (sem cortar o campo).
    const alignPoint = container.scrollLeft + stickyWidth
    const epsilon = 4

    if (direction > 0) {
      const next = columns.find((col) => col.offsetLeft > alignPoint + epsilon)
      if (!next) {
        container.scrollTo({
          left: container.scrollWidth - container.clientWidth,
          behavior: 'smooth',
        })
        return
      }
      container.scrollTo({
        left: Math.max(0, next.offsetLeft - stickyWidth),
        behavior: 'smooth',
      })
      return
    }

    const previous = [...columns]
      .reverse()
      .find((col) => col.offsetLeft < alignPoint - epsilon)
    if (!previous) {
      container.scrollTo({ left: 0, behavior: 'smooth' })
      return
    }
    container.scrollTo({
      left: Math.max(0, previous.offsetLeft - stickyWidth),
      behavior: 'smooth',
    })
  }

  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize

  return (
    <div className="px-6 pb-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner size="lg" className="mx-auto" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16">
            <Text size="lg" className="text-red-600 dark:text-red-400">
              {error}
            </Text>
          </div>
        ) : !hasSearched ? (
          <div className="flex items-center justify-center py-16">
            <Text color="muted">{emptyBeforeSearchMessage}</Text>
          </div>
        ) : isEmpty ? (
          <div className="flex items-center justify-center py-16">
            <Text color="muted">{emptyAfterSearchMessage}</Text>
          </div>
        ) : (
          <div className="px-4 py-5 sm:p-6">
            {toolbar || showHorizontalScroll ? (
              <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0 flex-1">{toolbar}</div>
                {showHorizontalScroll ? (
                  <div className="flex shrink-0 items-center gap-2 pb-0.5">
                    <button
                      type="button"
                      onClick={() => scrollListByColumns(-1)}
                      className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                      aria-label="Rolar para a esquerda"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollListByColumns(1)}
                      className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                      aria-label="Rolar para a direita"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div ref={listScrollRef} className={showHorizontalScroll ? 'overflow-x-auto' : ''}>
              {children}
            </div>

            <div className="px-2 py-4 border-t border-gray-200 dark:border-gray-700 mt-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                  <Text size="sm">
                    Mostrando {startIndex + 1} a {Math.min(endIndex, total)} de {total} registros
                  </Text>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Anterior
                    </button>
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNumber: number
                        if (totalPages <= 5) {
                          pageNumber = i + 1
                        } else if (currentPage <= 3) {
                          pageNumber = i + 1
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i
                        } else {
                          pageNumber = currentPage - 2 + i
                        }
                        return (
                          <button
                            key={pageNumber}
                            type="button"
                            onClick={() => onPageChange(pageNumber)}
                            className={`px-3 py-2 text-sm font-medium rounded-md ${
                              currentPage === pageNumber
                                ? 'bg-primary-600 text-white'
                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        )
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Próximo
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
